/* eslint-disable react/display-name */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite/no-important';
import { DataGrid, Drawer, TextInputWithLabel, Button, PopupMenu } from 'quantux-ui';
import WarningDialog from '../warning-dialog';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
import { BASE_URL, DRAWER_BREAK_POINT } from '../../utils';
import { blue, gray, salmon } from '../../theme';

let controller = new AbortController();

const defaultCellStyle = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  // borderBottom: '0.5px solid lime',
  // borderRight: '0.5px solid lime',
  lineHeight: '14px'
};

const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const DOCS_PER_PAGE = 10;

const headerDefs = [
  // Facility Name
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Facility Name',
    field: 'facilityName',
    key: 'facilityName',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 281, minWidth: 281, maxWidth: 281, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      const data = text.split('|');
      return (
        <div className='cursor__pointer fx fx__v-center'>
          <img
            src={data[1]}
            alt='Facility Avatar'
            className='cursor__pointer'
            style={{ maxHeight: '28px' }}
          />
          <div className='ml-2 cursor__pointer'>
            {data[0]}
          </div>
        </div>
      );
    }
  },
  // Mailing address ()
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Mailing Address',
    field: 'mailingAddress',
    key: 'mailingAddress',
    showFilterHint: true,
    isCustomFilter: true,
    filterHint: 'Enter city, state code, or ZIP',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // Format address and zip
      const addTmp = text.split(' ');
      let zip = addTmp.pop();
      zip = zip.length <= 5 ? zip : zip.substr(0, 5) + '-' + zip.substr(5, 9);
      // Generate Google map url
      // const addressUrl = "https://www.google.com/maps/search/?api=1&query=" + encodeURI(text);
      const addressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(`${addTmp.join(' ')} ${zip.substr(0, 5)}`);
      return (
        <div className="fx fx__v-center">
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
            style={{ fontSize: '16px' }}
          >
            <i className="fa fa-map-marker" />
          </a>
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
          >
            <div>
              {addTmp.join(' ') + ' ' + zip}
            </div>
          </a>
        </div>
      );
    }
  },
  // Phone
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Phone',
    field: 'phone',
    key: 'phone',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => (
      <div>
        <a
          href={`tel:+1${text}`}
        >
          {`${text.substr(0, 3)}-${text.substr(3, 3)}-${text.substr(6, 4)}`}
        </a>
      </div>
    )
  },
  // Locations
  // {
  //   visible: true,
  //   dataType: 'string',
  //   pin: 'none',
  //   sortable: false,
  //   sortOrder: 0,
  //   enableCellClick: false,
  //   text: 'Locations',
  //   field: 'locations',
  //   key: 'locations',
  //   isCustomFilter: false,
  //   showFilterHint: false,
  //   filter: false,
  //   filterType: 'none',
  //   disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
  //   filterMenuType: 'advanced',
  //   basicFilterType: 'contains',
  //   advancedFilterType: 'containsAny',
  //   colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
  //   headerCellStyle: {},
  //   colCellsStyles: { textAlign: 'left' },
  //   footerCellStyle: {}
  // },
  // Taxonomy
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Taxonomy',
    field: 'taxonomy',
    key: 'taxonomy',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 246, minWidth: 246, maxWidth: 246, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  }
];

const PracticeFacilitiesGrid = (props) => {
  const [showLoader, setShowLoader] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showGridEllipsisMenu, setShowGridEllipsisMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 926);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 143);
  const [isMobileLayout, setMobileLayout] = useState(false);
  // --
  const [showAddFacilityDrawer, setShowAddFacilityDrawer] = useState(false);
  const [isSearchingFacilities, setIsSearchingFacilities] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [facilitySearch, setFacilitySearch] = useState('');
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [practiceDisplayName, setPracticeDisplayName] = useState('');

  // React router params
  const { practiceNpi } = useParams();

  // -- Network Ops -----------------------------------------------------
  const getData = async () => {
    setShowLoader(true);

    const formData = new FormData();
    formData.append('practiceNpi', practiceNpi);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };
    const response = await fetch(`${BASE_URL}/practice/getPracticeFacilities`, requestOptions)
    const result = await response.json();
    console.log(result);

    if (result.practiceDisplayName) {
      setPracticeDisplayName(result.practiceDisplayName);
    }

    if (result.success) {
      const data = [];
      result.facilities.forEach(facility => {
        // const add1 = facility.mailingAddress.streetAddress1;
        // const suite = facility.mailingAddress.suite;
        // const city = facility.mailingAddress.city;
        // const state = facility.mailingAddress.state;
        // const zip = facility.mailingAddress.zip;
        // const accessType = facility.accessType;

        data.push({
          id: facility._id || facility.id,
          // facilityName: `${facility.displayName}|${facility.avatarImageUrl}`,
          facilityName: facility.facilityName,
          mailingAddress: facility.mailingAddress,
          phone: facility.phone ? facility.phone.replace(/-/g, '') : '',
          taxonomy: facility.taxonomy,
          locations: facility.locations.join(', ')
        });
      });

      setShowLoader(false);
      setGridData(data);
    }
    setShowLoader(false);
  };

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      // console.log(`gridParentRef width = ${gridParentRefWidth}`);
      // const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.75 * gridParentRefWidth);
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.98 * gridParentRefWidth);
      // console.log(`GridWidth = ${tmp}`);
      // setGridWidth(Math.min(tmp, 1111));
      setGridWidth(Math.min(tmp, 926));
      setGridHeight(window.innerHeight - 155);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  // -- Refs ------------------------------------------------------------
  const gridEllipsisMenu = useRef();
  const gridParentRef = useRef();

  // -- Effects ---------------------------------------------------------
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleWindowResize);
    getData();
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
    };
  }, []);

  // -- Styles ----------------------------------------------------------
  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: '40px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridContainer: {
      width: `${gridWidth}px`,
      overflow: 'hidden'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    drawerContainer: {
      // background: gray[500],
      background: gray[800],
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column',
      padding: '8px'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px 0 0 2px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      // height: 'calc(100vh - 186px)',
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '138px',
      paddingLeft: '8px',
      background: '#103A60',
      ':hover': {
        background: '#0066CC'
      }
    }
  });

  // #region -- Event Handlers ------------------------------------------
  const handleOnClickAddFacility = () => {
    setFacilitySearch('');
    setSearchResults([]);
    setIsSearchingFacilities(false);
    setShowAddFacilityDrawer(true);
  };

  const handleOnClickCell = () => { };

  const handleOnPopupMenu = (e, idx) => {
    // console.log(e.pageX, e.pageY, idx);
    setShowGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnEditFacilitySearch = (e) => {
    const { value } = e.target;
    setFacilitySearch(value);
  };

  const handleOnClickSearchFacilities = () => {
    if (isSearchingFacilities) {
      controller.abort();
      setIsSearchingFacilities(false);
      setFacilitySearch('');
      return;
    }

    setSearchResults([]);
    controller = new AbortController();
    const url = new URL(`${BASE_URL}/practice/searchFacilities`);
    const params = { searchTerm: facilitySearch };
    url.search = new URLSearchParams(params).toString();

    setIsSearchingFacilities(true);
    fetch(url, { signal: controller.signal })
      .then(response => response.text())
      .then(result => {
        const data = JSON.parse(result);
        if (data.success) {
          console.log(data);
          setIsSearchingFacilities(false);
          // data.users.sort((a, b) => b.hasMedprosProfile - a.hasMedprosProfile);
          setSearchResults(data.facilities);
        } else {
          setIsSearchingFacilities(false);
          setSearchResults([]);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickAddFacilityToPractice = async (facility) => {
    console.log(facility);

    const formData = new FormData();
    formData.append('practiceId', props.practiceId);
    formData.append('facilityId', facility._id);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const response = await fetch(`${BASE_URL}/practice/updatePracticeFacilities`, requestOptions);
    const result = await response.json();
    console.log(result);
    if (result.success) {
      setShowAddFacilityDrawer(false);
      getData();
    }
  };

  const handleOnClickDeleteFacility = async () => {
    setShowGridEllipsisMenu(false);
    const facilityId = gridData[popupMenuIndex].id;
    const formData = new FormData();
    formData.append('practiceNpi', practiceNpi);
    formData.append('facilityId', facilityId);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'DELETE',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const response = await fetch(`${BASE_URL}/practice/deletePracticeFacility`, requestOptions);
    const result = await response.json();
    console.log(result);

    if (result.success) {
      getData();
    }
  };
  // #endregion

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Add Facility Drawer */}
      {showAddFacilityDrawer &&
        <Drawer
          placement="right"
          visible={showAddFacilityDrawer}
          toggleDrawer={() => {
            setShowAddFacilityDrawer(false);
          }}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-hospital-o' />
                <div className='noselect'>Add Facility</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => {
                  setShowAddFacilityDrawer(false);
                }}
              />
            </div>
            {/* Facility Search */}
            <div>
              <TextInputWithLabel
                value={facilitySearch}
                titleText="Facility Name"
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={341}
                placeholder='Required'
                onChange={handleOnEditFacilitySearch}
              />
              <Button
                className='mt-4'
                isDisabled={facilitySearch.length === 0}
                showLoader={isSearchingFacilities}
                btnType='primary'
                onClick={handleOnClickSearchFacilities}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '32px'
                }}
              >
                Search Facilities
              </Button>
            </div>
            {/* Facility Cards */}
            <div>
              {searchResults.map((facility) => (
                <div
                  key={facility._id}
                  className='mt-2 pl-2 pr-2'
                  style={{
                    background: gray[700],
                    padding: '8px',
                    borderRadius: '4px'
                    // height: 'calc(100vh - 270px)',
                    // overflowY: 'auto'
                  }}
                >
                  {/* Data */}
                  <div style={{ flexGrow: '1' }}>
                    <div style={{ color: blue[400] }}>
                      <div>{facility.displayName}</div>
                      <div>{`NPI: ${facility.npi}`}</div>
                    </div>
                    <div className='mt-1'>
                      {facility.taxonomy}
                    </div>
                  </div>
                  {/* Icons/buttons */}
                  <div className='pr-1 fx' style={{ justifyContent: 'flex-end' }}>
                    <Button
                      style={{
                        maxWidth: '130px',
                        minWidth: '130px',
                        maxHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '4px',
                        fontSize: '14px'
                      }}
                      onClick={() => handleOnClickAddFacilityToPractice(facility)}
                    >
                      Add Facility
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Drawer>
      }

      {/* Facility Delete Warning Dialog */}
      {showDeleteWarning &&
          <WarningDialog
            show={showDeleteWarning}
            title='Delete Facility'
            message='Do you want to delete facility?'
            onCancel={() => setShowDeleteWarning(false)}
            onContinue={() => {
              setShowDeleteWarning(false);
              handleOnClickDeleteFacility();
            }}
          />
        }

      <div className={css(styles.gridContainer)}>
        {/* Grid Toolbar */}
        <div className={css(styles.gridToolbar)}>
          {/* Add provider button */}
          <div
            className={css(styles.gridToolbarIcon)}
            onClick={handleOnClickAddFacility}
          >
            <i className="fa fa-lg fa-plus-circle" />
          </div>
        </div>

        {/* Grid ellipsis menu */}
        <div
          ref={gridEllipsisMenu}
          className={css(styles.gridEllipsisMenuParent)}
          style={{
            top: cursorY,
            left: cursorX - 140,
            display: showGridEllipsisMenu ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position="relative"
            visible={showGridEllipsisMenu}
            parent={gridEllipsisMenu}
            onOutsideClick={() => { setShowGridEllipsisMenu(false); }}
          >
            <div className={css(styles.gridEllipsisMenuContainer)}>
              {/* Show details menu item */}
              <div className={css(styles.gridEllipsisMenuItem)}>
                Show Details
              </div>
              {/* Delete record menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={() => setShowDeleteWarning(true)}
              >
                Delete Record
              </div>
            </div>
          </PopupMenu>
        </div>

        {/* ---- Data Grid ---- */}
        <DataGrid
          loader={showLoader}
          icons={icons}
          headerDefs={headerDefs}
          rowData={gridData}
          zeroRowMessage={`Contact MedPros Admin to add ${practiceDisplayName}'s facilities.`}
          filters={filters}
          gridWidth={gridWidth}
          gridHeight={gridHeight}
          gridHeaderHeight={48}
          gridFooterHeight={0}
          gridContainerHeight="804px"
          rowHeight={40}
          rowsPerPage={DOCS_PER_PAGE}
          defaultCellStyle={defaultCellStyle}
          showFooter={false}
          showEllipsisCol
          enableHover
          totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
          currentPage={currentPage}
          isMobileLayout={isMobileLayout}
          onApplyFilter={() => console.log('not implemented yet!')}
          onResetCol={() => console.log('not implemented yet!')}
          onLoadPage={() => console.log('not implemented yet!')}
          onClickCell={handleOnClickCell}
          onColFilterToggle={() => console.log('not implemented yet!')}
          onFilterTypeUpdate={() => console.log('not implemented yet!')}
          onColPinChange={() => console.log('not implemented yet!')}
          onResize={() => console.log('on resize')}
          onPopupMenu={handleOnPopupMenu}
          onChangeTextAlignment={() => console.log('Text alignment changed!')}
          onFilterChange={() => console.log('on filter changed')}
          onColVisibilityChange={() => console.log('on col visibility changed')}
          onResetGrid={() => console.log('on reset grid')}
          onSort={() => { console.log('Column sort order changed!'); }}
          onColConfig={() => console.log('on col config changed')}
        />
      </div>
    </div>
  );
};

export default PracticeFacilitiesGrid;
